import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, Space, Typography, Select, DatePicker, Tag, Button, Row, Col,
  message, Modal, Input, Checkbox, Alert, Popconfirm, Spin, Tabs,
  Tooltip, ConfigProvider,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, BulbOutlined, EyeOutlined,
  SettingOutlined, ArrowLeftOutlined,
} from '@ant-design/icons';
import MultiCampaignReport from '../MultiCampaignReport';
import AIInsightsDrawer from './AIInsightsDrawer';
import './NewReportPage.css';
import dayjs from 'dayjs';

const { Text } = Typography;

const NR_THEME = {
  token: { colorPrimary: '#2E86AB', colorLink: '#2E86AB', borderRadius: 6 },
  components: {
    Card: { headerFontSize: 15 },
  },
};

/**
 * NewReportPage — tab-based multi-campaign report builder.
 *
 * Props (passed from Reports.js via mcStore):
 *   store    — all mc* state + handlers (see Reports.js mcStore shape)
 *   onBack   — () => void
 *   onReportSaved — () => void  (refresh saved reports list)
 */
const NewReportPage = ({ store, onBack, onReportSaved }) => {
  const {
    reportName, setReportName,
    month, setMonth,
    managingGroups, setManagingGroups,
    allManagingGroups,
    groupOwner, setGroupOwner,
    allGroupOwners,
    campaigns, campaignsLoading, campaignOptions,
    selectedCampaignIds, setSelectedCampaignIds,
    reportData, setReportData,
    reportLoading, backendReportLoading,
    editableGroups, setEditableGroups,
    availableDepartments,
    pdfOptions, setPdfOptions,
    availableDeptPresets,
    loadedPresetName, setLoadedPresetName,
    loadedFromReport, setLoadedFromReport,
    customGroupModalOpen, setCustomGroupModalOpen,
    customGroupName, setCustomGroupName,
    customGroupDepts, setCustomGroupDepts,
    savePresetModalOpen, setSavePresetModalOpen,
    savePresetName, setSavePresetName,
    onLoadCampaigns,
    onGenerateReport,
    onLoadPreset,
    onUpdatePreset,
    onDeletePreset,
    onSaveAsPreset,
    onAddCustomGroup,
    onRemoveGroup,
    onSaveReport,
    saving: storeSaving,
    formatPercentDisplay,
    formatNumberDisplay,
    percentValue,
  } = store;

  // ── Local state ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('setup');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [aiInsights, setAiInsights] = useState({});
  const [dirty, setDirty] = useState(false);
  const configSnapshotRef = useRef(null);

  // ── Derived ────────────────────────────────────────────────────────────────
  const dataLoaded = !!(reportData?._rawData);
  const reportReady = !!(reportData?.departmentGroupConfig);

  const deptCount = {};
  editableGroups.forEach(g => (g.departments || []).forEach(d => { deptCount[d] = (deptCount[d] || 0) + 1; }));
  const unassignedCount = availableDepartments.filter(d => !deptCount[d]).length;
  const duplicatedCount = availableDepartments.filter(d => deptCount[d] > 1).length;

  const effectiveNetworkName = pdfOptions.networkName || groupOwner || (managingGroups.length === 1 ? managingGroups[0] : '');

  const lastConfig = (() => { try { return JSON.parse(localStorage.getItem('mcLastConfig')); } catch { return null; } })();

  // ── Dirty tracking ─────────────────────────────────────────────────────────
  const configFingerprint = useCallback(() => JSON.stringify({
    ids: selectedCampaignIds,
    month: month?.valueOf(),
    groups: managingGroups,
    egIds: editableGroups.map(g => g.id + ':' + (g.departments || []).join(',')),
  }), [selectedCampaignIds, month, managingGroups, editableGroups]);

  useEffect(() => {
    const fp = configFingerprint();
    if (configSnapshotRef.current === null) {
      configSnapshotRef.current = fp;
      return;
    }
    if (fp !== configSnapshotRef.current) setDirty(true);
  }, [configFingerprint]);

  // Reset dirty when a fresh report is generated
  useEffect(() => {
    if (reportReady) {
      setDirty(false);
      configSnapshotRef.current = configFingerprint();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportData]);

  // Auto-apply the last-used preset once the preset list is available
  useEffect(() => {
    if (availableDeptPresets.length > 0 && loadedPresetName && editableGroups.length === 0) {
      onLoadPreset(loadedPresetName);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDeptPresets]);

  // Switch to preview tab after generation
  useEffect(() => {
    if (reportReady && activeTab === 'setup') {
      setActiveTab('preview');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportReady]);

  // ── Open in new tab ────────────────────────────────────────────────────────
  const openInNewTab = () => {
    sessionStorage.setItem('mcReportData', JSON.stringify(reportData));
    sessionStorage.setItem('mcReportName', reportName || 'Multi-Campaign Report');
    sessionStorage.setItem('mcReportCustomisation', JSON.stringify({
      networkName: effectiveNetworkName,
      reportLabel: pdfOptions.reportLabel,
      orgLabel: pdfOptions.orgLabel,
      orgLabelPlural: pdfOptions.orgLabelPlural,
      deptLabel: pdfOptions.deptLabel,
      deptLabelPlural: pdfOptions.deptLabelPlural,
    }));
    sessionStorage.removeItem('mcSavedReportId');
    sessionStorage.removeItem('mcSavedAiInsights');
    window.open('/report-view', '_blank');
  };

  // ── Restore from last-config banner ───────────────────────────────────────
  const restoreLastConfig = () => {
    if (!lastConfig) return;
    if (lastConfig.managingGroups) setManagingGroups(lastConfig.managingGroups);
    if (lastConfig.groupOwner) {
      setGroupOwner(lastConfig.groupOwner);
      const owner = allGroupOwners?.find(o => o.name === lastConfig.groupOwner);
      if (owner?.managingGroups?.length) setManagingGroups(owner.managingGroups);
    }
    if (lastConfig.month) setMonth(dayjs(lastConfig.month, 'MMMM YYYY'));
    if (lastConfig.reportName) setReportName(lastConfig.reportName);
    if (lastConfig.pdfOptions) setPdfOptions(prev => ({ ...prev, ...lastConfig.pdfOptions }));
    if (lastConfig.selectedCampaignIds?.length) setSelectedCampaignIds(lastConfig.selectedCampaignIds);
    if (lastConfig.loadedPresetName) setLoadedPresetName(lastConfig.loadedPresetName);
  };

  // ── Handle generate (wraps parent handler, auto-switches tab) ─────────────
  const handleGenerate = async () => {
    await onGenerateReport();
    // Tab switch is handled by the useEffect above watching reportReady
  };

  // ── Handle explicit save ───────────────────────────────────────────────────
  const handleSave = () => {
    if (onSaveReport) onSaveReport(aiInsights);
  };

  // ── TOC sections ──────────────────────────────────────────────────────────
  const tocSections = reportReady
    ? [
        { id: 'mc-kpis',    label: 'Overview KPIs' },
        { id: 'mc-leaderboard', label: 'Leaderboard' },
        ...editableGroups.map(g => ({ id: `mc-group-${g.id}`, label: g.label || g.name, sub: true })),
      ]
    : [];

  const [activeTocId, setActiveTocId] = useState(null);

  const handleTocClick = (id) => {
    setActiveTocId(id);
    const el = document.getElementById(id);
    if (!el) return;
    // Walk up to find the nearest scrollable ancestor
    const getScrollParent = (node) => {
      if (!node || node === document.body) return null;
      const { overflow, overflowY } = window.getComputedStyle(node);
      if (/(auto|scroll)/.test(overflow + overflowY) && node.scrollHeight > node.clientHeight) return node;
      return getScrollParent(node.parentElement);
    };
    const container = getScrollParent(el.parentElement);
    if (container) {
      const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 16;
      container.scrollTo({ top, behavior: 'smooth' });
    } else {
      const top = window.pageYOffset + el.getBoundingClientRect().top - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  // ── Tab items ──────────────────────────────────────────────────────────────
  const setupTabLabel = (
    <Space size={6}>
      <span className={`step-tag ${dataLoaded ? 'done' : selectedCampaignIds.length ? '' : 'idle'}`}>1</span>
      Setup
    </Space>
  );

  const previewTabLabel = (
    <Space size={6}>
      <span className={`step-tag ${reportReady ? 'done' : 'idle'}`}>2</span>
      Preview
      {reportReady && <Tag color="green" style={{ fontSize: 10, margin: 0 }}>Ready</Tag>}
    </Space>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <ConfigProvider theme={NR_THEME}>
      {/* ── Repeat-last banner ── */}
      {lastConfig && !loadedFromReport && (
        <div className={`last-banner${dirty ? ' dim' : ''}`}>
          <div>
            <div className="last-banner__label">Last report</div>
            <div className="last-banner__title">
              {lastConfig.reportName}{lastConfig.month ? ' · ' + lastConfig.month : ''}
            </div>
          </div>
          {!dirty && (
            <Button
              type="primary"
              size="small"
              style={{ background: 'rgba(255,255,255,0.2)', borderColor: 'rgba(255,255,255,0.4)', color: '#fff' }}
              onClick={restoreLastConfig}
            >
              Restore settings
            </Button>
          )}
        </div>
      )}

      {/* ── Loaded-from banner ── */}
      {loadedFromReport && (
        <div className="loaded-from-banner">
          <Text style={{ fontSize: 13 }}>
            <strong>Loaded from:</strong> {loadedFromReport.title}
            {' — select campaigns for the new month and click Load Campaigns.'}
          </Text>
          <Button size="small" onClick={() => setLoadedFromReport(null)}>Dismiss</Button>
        </div>
      )}

      {/* ── Dirty notice ── */}
      {dirty && reportReady && (
        <div className="dirty-notice">
          <span>&#9888;</span>
          Config changed since last generation — regenerate to update the preview.
        </div>
      )}

      <Card
        title="Multi-Campaign Report"
        extra={
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            Back to Reports
          </Button>
        }
        bodyStyle={{ paddingTop: 8 }}
      >
        <Tabs
          className="nr-tabs"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'setup',   label: setupTabLabel,   children: <SetupPanel store={store} deptCount={deptCount} unassignedCount={unassignedCount} duplicatedCount={duplicatedCount} dataLoaded={dataLoaded} reportReady={reportReady} dirty={dirty} handleGenerate={handleGenerate} openInNewTab={openInNewTab} /> },
            { key: 'preview', label: previewTabLabel, children: <PreviewPanel reportData={reportData} reportName={reportName} reportReady={reportReady} effectiveNetworkName={effectiveNetworkName} pdfOptions={pdfOptions} formatPercentDisplay={formatPercentDisplay} formatNumberDisplay={formatNumberDisplay} percentValue={percentValue} tocSections={tocSections} activeTocId={activeTocId} onTocClick={handleTocClick} drawerOpen={drawerOpen} onDrawerOpen={() => setDrawerOpen(true)} onDrawerClose={() => setDrawerOpen(false)} aiInsights={aiInsights} onInsightsSaved={setAiInsights} editableGroups={editableGroups} openInNewTab={openInNewTab} dirty={dirty} onGenerateReport={handleGenerate} reportLoading={reportLoading} onSave={handleSave} saving={storeSaving} /> },
          ]}
        />
      </Card>

      {/* ── Modals ── */}
      <Modal
        title="Add Custom Department Group"
        open={customGroupModalOpen}
        onOk={onAddCustomGroup}
        onCancel={() => { setCustomGroupModalOpen(false); setCustomGroupName(''); setCustomGroupDepts([]); }}
        okText="Create Group"
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Group Name</Text>
            <Input placeholder="e.g., IT Team" value={customGroupName} onChange={e => setCustomGroupName(e.target.value)} />
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Select Departments</Text>
            <Row gutter={[8, 8]}>
              {availableDepartments.map(dept => (
                <Col key={dept} span={12}>
                  <Checkbox
                    checked={customGroupDepts.includes(dept)}
                    onChange={e => {
                      if (e.target.checked) setCustomGroupDepts([...customGroupDepts, dept]);
                      else setCustomGroupDepts(customGroupDepts.filter(d => d !== dept));
                    }}
                  >{dept}</Checkbox>
                </Col>
              ))}
            </Row>
          </div>
        </Space>
      </Modal>

      <Modal
        title="Save Current Groups as Preset"
        open={savePresetModalOpen}
        onOk={onSaveAsPreset}
        onCancel={() => { setSavePresetModalOpen(false); setSavePresetName(''); }}
        okType="default" okText="Save"
      >
        <Input
          placeholder="Preset name (e.g. MBSA Standard)"
          value={savePresetName}
          onChange={e => setSavePresetName(e.target.value)}
        />
      </Modal>
    </ConfigProvider>
  );
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* SetupPanel — left: config cards, right: sticky rail                        */
/* ─────────────────────────────────────────────────────────────────────────── */
const SetupPanel = ({
  store, deptCount, unassignedCount, duplicatedCount,
  dataLoaded, reportReady, dirty, handleGenerate, openInNewTab,
}) => {
  const {
    reportName, setReportName,
    month, setMonth,
    managingGroups, setManagingGroups,
    allManagingGroups,
    groupOwner, setGroupOwner,
    allGroupOwners,
    campaignOptions, campaignsLoading, campaigns,
    selectedCampaignIds, setSelectedCampaignIds,
    setReportData,
    reportLoading, backendReportLoading,
    editableGroups,
    availableDepartments,
    pdfOptions, setPdfOptions,
    availableDeptPresets,
    loadedPresetName, setLoadedPresetName,
    setCustomGroupModalOpen,
    setSavePresetModalOpen,
    setSavePresetName,
    onLoadCampaigns,
    onLoadPreset,
    onUpdatePreset,
    onDeletePreset,
    onUpdateGroupDepartments,
    onRemoveGroup,
    pdfProfiles,
  } = store;

  const canGenerate = dataLoaded && editableGroups.length > 0;

  const primaryBtnLabel = !dataLoaded
    ? 'Load Campaigns'
    : reportReady
    ? (dirty ? 'Regenerate Report' : 'Regenerate Report')
    : 'Generate Report';

  const primaryBtnAction = !dataLoaded ? onLoadCampaigns : handleGenerate;
  const primaryBtnDisabled = !dataLoaded ? !selectedCampaignIds.length : !canGenerate;
  const primaryBtnLoading = !dataLoaded ? backendReportLoading : reportLoading;

  const statusText = !selectedCampaignIds.length
    ? 'Select campaigns to get started'
    : !dataLoaded
    ? 'Click Load Campaigns to fetch data'
    : reportReady
    ? 'Report ready — regenerate or open'
    : 'Configure dept groups, then Generate';

  return (
    <Row gutter={24} align="top">
      {/* ── Left: config ── */}
      <Col xs={24} lg={16}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>

          {/* Source & Scope */}
          <div className="nr-section">
            <Text className="nr-section-title">Source &amp; Scope</Text>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>Report Name</Text>
                <Input
                  value={reportName}
                  onChange={e => setReportName(e.target.value)}
                  placeholder="e.g. National Performance Report — May 2026"
                />
              </div>

              <Row gutter={12}>
                <Col span={8}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>Group Owner</Text>
                  <Select
                    allowClear
                    placeholder="Filter by owner..."
                    style={{ width: '100%' }}
                    value={groupOwner}
                    onChange={v => {
                      setGroupOwner(v);
                      if (v) {
                        const owner = allGroupOwners.find(o => o.name === v);
                        setManagingGroups(owner?.managingGroups?.length ? [...owner.managingGroups] : []);
                      } else {
                        setManagingGroups([]);
                      }
                      setSelectedCampaignIds([]);
                      setReportData(null);
                    }}
                    options={allGroupOwners.map(o => ({ label: o.name, value: o.name }))}
                  />
                </Col>
                <Col span={8}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>Managing Groups</Text>
                  <Select
                    mode="multiple"
                    placeholder="Select group(s)..."
                    style={{ width: '100%' }}
                    value={managingGroups}
                    onChange={v => { setManagingGroups(v); setSelectedCampaignIds([]); setReportData(null); }}
                    options={(groupOwner
                      ? (allGroupOwners.find(o => o.name === groupOwner)?.managingGroups || [])
                      : allManagingGroups
                    ).map(g => ({ label: g, value: g }))}
                    allowClear
                    maxTagCount="responsive"
                  />
                </Col>
                <Col span={8}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>Month</Text>
                  <DatePicker
                    picker="month"
                    style={{ width: '100%' }}
                    value={month}
                    onChange={v => { setMonth(v); setSelectedCampaignIds([]); setReportData(null); }}
                    placeholder="Select month"
                  />
                </Col>
              </Row>

              {managingGroups.length > 0 && month && (
                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>Campaigns</Text>
                  <Select
                    mode="multiple"
                    placeholder="Choose campaigns..."
                    style={{ width: '100%' }}
                    value={selectedCampaignIds}
                    onChange={v => { setSelectedCampaignIds(v); setReportData(null); }}
                    options={campaignOptions}
                    loading={campaignsLoading}
                    maxTagCount="responsive"
                    showSearch
                    filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                    optionRender={(opt) => (
                      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <span>{opt.data.label}</span>
                        <Space>
                          <Tag color="blue">{opt.data.organization}</Tag>
                          <Tag color={opt.data.statusColor}>{opt.data.status}</Tag>
                        </Space>
                      </Space>
                    )}
                  />
                  {!campaignsLoading && campaigns.length === 0 && managingGroups.length > 0 && (
                    <Alert
                      message="No campaigns found"
                      description={`No campaigns for ${managingGroups.join(', ')} in ${month?.format('MMMM YYYY')}`}
                      type="warning" showIcon style={{ marginTop: 8 }}
                    />
                  )}
                </div>
              )}
            </Space>
          </div>

          {/* Department Groups */}
          <div className="nr-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Text className="nr-section-title" style={{ marginBottom: 0 }}>Department Groups</Text>
              {dataLoaded && (
                <Space size={6}>
                  <Select
                    placeholder="Load preset..."
                    style={{ width: 160 }}
                    allowClear
                    size="small"
                    options={availableDeptPresets.map(p => ({ label: p.name, value: p.name }))}
                    onChange={val => { if (val) onLoadPreset(val); else setLoadedPresetName(null); }}
                    value={loadedPresetName}
                  />
                  {loadedPresetName ? (
                    <>
                      <Button size="small" type="primary" onClick={onUpdatePreset}>Update</Button>
                      <Popconfirm title={`Delete "${loadedPresetName}"?`} onConfirm={onDeletePreset} okText="Delete" okType="danger">
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </>
                  ) : (
                    <Button size="small" onClick={() => { setSavePresetName(''); setSavePresetModalOpen(true); }}>Save preset</Button>
                  )}
                  <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setCustomGroupModalOpen(true)}>Add Group</Button>
                </Space>
              )}
            </div>

            {!dataLoaded ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: '#bbb', fontSize: 13 }}>
                Select campaigns above and click <strong>Load Campaigns</strong> to configure department groups.
              </div>
            ) : (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {/* Coverage summary */}
                <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 6, padding: '10px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <Text style={{ fontSize: 12 }}>{availableDepartments.length} departments detected</Text>
                    <div style={{ fontSize: 11, display: 'flex', gap: 12 }}>
                      {unassignedCount > 0 && <span style={{ color: '#ff4d4f' }}>&#10007; {unassignedCount} unassigned</span>}
                      {duplicatedCount > 0 && <span style={{ color: '#fa8c16' }}>&#9888; {duplicatedCount} duplicated</span>}
                      {unassignedCount === 0 && duplicatedCount === 0 && availableDepartments.length > 0 && (
                        <span style={{ color: '#52c41a' }}>&#10003; All covered</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {availableDepartments.map(dept => {
                      const count = deptCount[dept] || 0;
                      const cls = count === 0 ? 'err' : count > 1 ? 'warn' : 'ok';
                      const icon = count === 0 ? '&#10007;' : count > 1 ? '&#9888;' : '&#10003;';
                      return (
                        <span key={dept} className={`dept-chip ${cls}`} dangerouslySetInnerHTML={{ __html: `${icon} ${dept}` }} />
                      );
                    })}
                  </div>
                </div>

                {/* Group cards */}
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {editableGroups.map(group => (
                    <Card key={group.id} size="small" style={{ background: group.isCustom ? '#e6f7ff' : '#fafafa' }}>
                      <Row justify="space-between" align="top">
                        <Col span={20}>
                          <Text strong style={{ fontSize: 13 }}>{group.label || group.name}</Text>
                          <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{group.departments?.length || 0} depts</Text>
                          <Row gutter={[8, 6]} style={{ marginTop: 8 }}>
                            {availableDepartments.map(dept => {
                              const isChecked = group.departments?.includes(dept) || false;
                              const isDup = isChecked && editableGroups.some(g => g.id !== group.id && g.departments?.includes(dept));
                              return (
                                <Col key={dept} span={8}>
                                  <Checkbox
                                    checked={isChecked}
                                    onChange={e => {
                                      const newDepts = e.target.checked
                                        ? [...(group.departments || []), dept]
                                        : (group.departments || []).filter(d => d !== dept);
                                      onUpdateGroupDepartments(group.id, newDepts);
                                    }}
                                  >
                                    <span style={{ fontSize: 12, ...(isDup ? { color: '#fa8c16', fontWeight: 600 } : {}) }}>
                                      {dept}{isDup ? ' !' : ''}
                                    </span>
                                  </Checkbox>
                                </Col>
                              );
                            })}
                          </Row>
                        </Col>
                        <Col span={4} style={{ textAlign: 'right' }}>
                          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => onRemoveGroup(group.id)} />
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </Space>
              </Space>
            )}
          </div>

          {/* Advanced labels */}
          <details className="nr-section" style={{ padding: 0 }}>
            <summary style={{ padding: '12px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600, userSelect: 'none' }}>
              Advanced &middot; Labels
            </summary>
            <div style={{ padding: '4px 20px 16px' }}>
              {pdfProfiles?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>Load from saved profile</Text>
                  <Select
                    size="small"
                    placeholder="Select a profile..."
                    style={{ width: 240 }}
                    allowClear
                    onChange={name => {
                      if (!name) return;
                      const profile = pdfProfiles.find(p => p.name === name);
                      if (profile) {
                        setPdfOptions(prev => ({
                          ...prev,
                          reportLabel: profile.reportLabel || prev.reportLabel,
                          networkName: profile.networkName || prev.networkName,
                          orgLabel: profile.orgLabel || prev.orgLabel,
                          orgLabelPlural: profile.orgLabelPlural || prev.orgLabelPlural,
                          deptLabel: profile.deptLabel || prev.deptLabel,
                          deptLabelPlural: profile.deptLabelPlural || prev.deptLabelPlural,
                          clientLogoUrl: profile.clientLogoUrl || prev.clientLogoUrl,
                        }));
                      }
                    }}
                    options={pdfProfiles.map(p => ({ label: p.name, value: p.name }))}
                  />
                </div>
              )}
              <Row gutter={[12, 12]}>
                {[
                  ['orgLabel',      'Organisation (singular)', 'Dealership'],
                  ['orgLabelPlural','Organisation (plural)',   'Dealerships'],
                  ['deptLabel',     'Role/Dept (singular)',    'Department'],
                  ['deptLabelPlural','Role/Dept (plural)',     'Departments'],
                ].map(([key, label, ph]) => (
                  <Col key={key} span={6}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>{label}</Text>
                    <Input size="small" placeholder={ph} value={pdfOptions[key]} onChange={e => setPdfOptions(prev => ({ ...prev, [key]: e.target.value }))} />
                  </Col>
                ))}
              </Row>
              <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>Network / Report Label</Text>
                  <Input size="small" placeholder="National Performance Report" value={pdfOptions.reportLabel} onChange={e => setPdfOptions(prev => ({ ...prev, reportLabel: e.target.value }))} />
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4, fontSize: 11 }}>Network Name (override)</Text>
                  <Input size="small" placeholder={groupOwner || (managingGroups[0] || '')} value={pdfOptions.networkName} onChange={e => setPdfOptions(prev => ({ ...prev, networkName: e.target.value }))} />
                </Col>
              </Row>
            </div>
          </details>

        </Space>
      </Col>

      {/* ── Right: sticky rail ── */}
      <Col xs={24} lg={8}>
        <div className="rail-sticky">
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <div className="nr-section">
              <Text className="nr-section-title">Generate</Text>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>{statusText}</Text>
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                <Button
                  type="primary"
                  block
                  size="large"
                  disabled={primaryBtnDisabled}
                  loading={primaryBtnLoading}
                  onClick={primaryBtnAction}
                >
                  {primaryBtnLabel}
                </Button>
                {dataLoaded && (
                  <Button
                    block
                    size="small"
                    loading={backendReportLoading}
                    onClick={onLoadCampaigns}
                  >
                    Reload campaign data
                  </Button>
                )}
                {reportReady && (
                  <Button block icon={<EyeOutlined />} onClick={openInNewTab}>
                    Open in New Tab
                  </Button>
                )}
              </Space>
            </div>

            {dataLoaded && (
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: '12px 16px', background: '#fafafa' }}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  {[
                    ['Campaigns', selectedCampaignIds.length],
                    ['Dept groups', editableGroups.length],
                    ['Month', month?.format('MMM YYYY') || '—'],
                    ['Managing groups', managingGroups.length || '—'],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <Text type="secondary">{label}</Text>
                      <Text>{val}</Text>
                    </div>
                  ))}
                </Space>
              </div>
            )}
          </Space>
        </div>
      </Col>
    </Row>
  );
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* PreviewPanel — TOC + report viewer + AI Insights Drawer                    */
/* ─────────────────────────────────────────────────────────────────────────── */
const PreviewPanel = ({
  reportData, reportName, reportReady, effectiveNetworkName, pdfOptions,
  formatPercentDisplay, formatNumberDisplay, percentValue,
  tocSections, activeTocId, onTocClick,
  drawerOpen, onDrawerOpen, onDrawerClose,
  aiInsights, onInsightsSaved,
  editableGroups, openInNewTab,
  dirty, onGenerateReport, reportLoading,
  onSave, saving,
}) => {
  if (!reportReady) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0', color: '#bbb' }}>
        <EyeOutlined style={{ fontSize: 40, marginBottom: 12 }} />
        <div style={{ fontSize: 14 }}>
          {dirty
            ? 'Config has changed — regenerate the report to see a preview.'
            : 'Complete setup and generate the report to see the preview here.'}
        </div>
        {dirty && (
          <Button type="primary" style={{ marginTop: 16 }} loading={reportLoading} onClick={onGenerateReport}>
            Regenerate Report
          </Button>
        )}
      </div>
    );
  }

  const overview = reportData?.overview || {};

  return (
    <>
      {/* ── Preview header bar ── */}
      <div style={{
        background: 'linear-gradient(135deg, #1A3A6B 0%, #267a96 100%)',
        borderRadius: '6px 6px 0 0',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: 1.5 }}>
            TapLearnGo
          </div>
          <div style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.2)' }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
            {reportName || 'Multi-Campaign Report'}
          </div>
        </div>
        <Space>
          <Button
            size="small"
            icon={<BulbOutlined />}
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}
            onClick={onDrawerOpen}
          >
            AI Insights
          </Button>
          <Button
            size="small"
            icon={<EyeOutlined />}
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}
            onClick={openInNewTab}
          >
            Open in New Tab
          </Button>
          <Button
            size="small"
            loading={saving}
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', borderColor: 'rgba(255,255,255,0.3)' }}
            onClick={onSave}
          >
            Save Report
          </Button>
        </Space>
      </div>

      {/* ── KPI strip ── */}
      <div style={{ background: '#fff', padding: '16px 20px', borderBottom: '1px solid #f0f0f0' }}>
        <div className="ps-kpis">
          {[
            { label: 'Completion Rate', value: overview.completionRate != null ? `${Math.round(overview.completionRate)}%` : '—' },
            { label: 'Avg Score', value: overview.averageScore != null ? `${Math.round(overview.averageScore)}%` : '—' },
            { label: 'Participants', value: overview.totalParticipants ?? '—' },
            { label: 'Campaigns', value: reportData?.campaigns?.length ?? '—' },
          ].map(kpi => (
            <div key={kpi.label} className="ps-kpi">
              <div className="ps-kpi__label">{kpi.label}</div>
              <div className="ps-kpi__value">{kpi.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Report body with TOC ── */}
      <div className="preview-shell">
        <div className="preview-wrap">
          {/* TOC sidebar */}
          {tocSections.length > 0 && (
            <div className="preview-toc">
              <h5>Contents</h5>
              {tocSections.map(sec => (
                <a
                  key={sec.id}
                  className={`toc-item${sec.sub ? ' toc-sub' : ''}${activeTocId === sec.id ? ' active' : ''}`}
                  href={`#${sec.id}`}
                  onClick={e => { e.preventDefault(); onTocClick(sec.id); }}
                >
                  {sec.label}
                </a>
              ))}
            </div>
          )}

          {/* Report content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <MultiCampaignReport
              aggregates={reportData}
              loading={false}
              formatPercentDisplay={formatPercentDisplay}
              formatNumberDisplay={formatNumberDisplay}
              percentValue={percentValue}
              reportName={reportName}
              savedReportId={null}
              initialAiInsights={null}
              networkName={effectiveNetworkName}
              reportLabel={pdfOptions.reportLabel}
              orgLabel={pdfOptions.orgLabel}
              orgLabelPlural={pdfOptions.orgLabelPlural}
              deptLabel={pdfOptions.deptLabel}
              deptLabelPlural={pdfOptions.deptLabelPlural}
            />
          </div>
        </div>
      </div>

      {/* ── AI Insights Drawer ── */}
      <AIInsightsDrawer
        open={drawerOpen}
        onClose={onDrawerClose}
        editableGroups={editableGroups}
        reportData={reportData}
        savedInsights={aiInsights}
        onInsightsSaved={onInsightsSaved}
        networkName={effectiveNetworkName}
        period={pdfOptions?.reportLabel}
      />
    </>
  );
};

export default NewReportPage;
